import { useEffect, useState, useRef } from 'react';
import { ArrowLeft, Volume2, SwitchCamera, Video, MicOff, X } from 'lucide-react';
import { Capacitor } from '@capacitor/core';

export default function ActiveCall({
  calleeName,
  calleeImage,
  status = 'dialing',
  startTimestamp = null,
  onEndCall,
  localStream,
  remoteStream,
  initialVideo = false,
  onSwapCamera,
  localFacing = 'user', // 'user' | 'environment'
}) {
  const [elapsed, setElapsed] = useState(0);
  const [muted, setMuted] = useState(false);
  const [speakerOn, setSpeakerOn] = useState(false);
  const [videoOn, setVideoOn] = useState(false);

  const remoteVideoRef = useRef(null);
  const localVideoRef = useRef(null);

  const toggleMute = () => {
    if (!localStream?.current) return;
    localStream.current.getAudioTracks().forEach(track => {
      track.enabled = !track.enabled;
    });
    setMuted(!muted);
  };

  const toggleVideo = () => {
    if (!localStream?.current) return;
    const vids = localStream.current.getVideoTracks();
    if (!vids.length) return;
    const next = !vids[0].enabled;
    vids.forEach(track => (track.enabled = next));
    setVideoOn(next);
  };

  useEffect(() => {
    if (status === 'dialing') {
      const timeoutId = setTimeout(() => {
        onEndCall();
      }, 30000); // 30 seconds
      return () => clearTimeout(timeoutId);
    }
  }, [status]);

  useEffect(() => {
    let intervalId;
    if (status === 'connected' && startTimestamp) {
      intervalId = setInterval(() => {
        setElapsed(Math.floor((Date.now() - startTimestamp) / 1000));
      }, 1000);
    }
    else {
      setElapsed(0);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [status, startTimestamp]);

  useEffect(() => {
    if (remoteVideoRef.current && remoteStream) {
      remoteVideoRef.current.srcObject = remoteStream;
    }
    if (localVideoRef.current && localStream?.current) {
      localVideoRef.current.srcObject = localStream.current;
    }
  }, [remoteStream, localStream]);

  useEffect(() => {
    const hasEnabled = !!localStream?.current && localStream.current.getVideoTracks().some(t => t.enabled);
    setVideoOn(initialVideo || hasEnabled);
  }, [initialVideo, localStream]);

  useEffect(() => {
    let audio;
    let intervalId;
    if (status === 'dialing') {
      audio = new Audio("/assets/sounds/call.mp3");
      audio.play();
      intervalId = setInterval(() => {
        audio.currentTime = 0;
        audio.play();
      }, 4000);
    }
    return () => {
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
      if (intervalId) clearInterval(intervalId);
    };
  }, [status]);

  const format = s => {
    const hh = Math.floor(s / 3600);
    const mm = Math.floor((s % 3600) / 60);
    const ss = s % 60;

    if (hh > 0) {
      return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
    }
    return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  };

  const rightText = status === 'dialing' ? 'Calling…' : format(elapsed);

  // Show some controls only on native (Capacitor) platforms
  const isNative = (() => {
    try {
      const platform = Capacitor?.getPlatform?.();
      return platform === 'ios' || platform === 'android';
    } catch (e) {
      return false;
    }
  })();

  return (
    <div className="tailwind">
      <div className="relative h-screen w-full bg-black text-white overflow-hidden">
        <div className="fixed top-0 left-0 right-0 z-20 pt-12 px-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onEndCall}
                className="w-12 h-12 rounded-full bg-black/60 backdrop-blur flex items-center justify-center"
                aria-label="Back"
              >
                <ArrowLeft className="w-7 h-7 text-white" />
              </button>
              <div className="flex items-center gap-2 bg-black/60 backdrop-blur rounded-full pl-1.5 pr-3 py-2.5">
                <img
                  src={calleeImage || '/assets/images/default_profile.png'}
                  alt={`${calleeName}'s profile`}
                  className="w-6 h-6 rounded-full object-cover"
                />
                <span className="font-poppins text-base font-medium truncate max-w-[40vw]">{calleeName}</span>
              </div>
              <div className="bg-black/60 backdrop-blur rounded-full px-3 py-2.5">
                <span className="text-sm text-gray-200">{rightText}</span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {isNative && (
                <button
                  onClick={onSwapCamera}
                  className="w-12 h-12 rounded-full bg-black/60 backdrop-blur flex items-center justify-center"
                  aria-label="Switch Camera"
                >
                  <SwitchCamera className="w-7 h-7" />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="absolute inset-0 bg-black">
          <div className="relative w-full h-full">
            <video
              ref={remoteVideoRef}
              autoPlay
              playsInline
              className="w-full h-full object-cover bg-black"
            />
            <div className="absolute bottom-28 right-4 w-28 h-40 rounded-xl overflow-hidden shadow-lg">
              <video
                ref={localVideoRef}
                autoPlay
                muted
                playsInline
                className={`w-full h-full object-cover ${localFacing === 'user' ? 'transform scale-x-[-1]' : ''}`}
              />
            </div>
          </div>
        </div>

        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-20 w-fit">
          <div className="h-16 bg-black/60 backdrop-blur rounded-full flex items-center justify-center gap-9 px-3">
            <button onClick={toggleVideo} className={`w-12 h-12 rounded-full flex items-center justify-center ${videoOn ? 'bg-white/15' : 'bg-transparent'}`} aria-label="Video">
              <Video className="w-7 h-7" />
            </button>
            <button onClick={toggleMute} className={`w-12 h-12 rounded-full flex items-center justify-center ${muted ? 'bg-white/15' : 'bg-transparent'}`} aria-label="Mute">
              <MicOff className="w-7 h-7" />
            </button>
            {isNative && (
              <button onClick={() => setSpeakerOn(s => !s)} className={`w-12 h-12 rounded-full flex items-center justify-center ${speakerOn ? 'bg-white/15' : 'bg-transparent'}`} aria-label="Speaker">
                <Volume2 className="w-7 h-7" />
              </button>
            )}
            <button onClick={onEndCall} className="w-12 h-12 rounded-full bg-red-600 hover:bg-red-700 active:bg-red-800 flex items-center justify-center" aria-label="End Call">
              <X className="w-7 h-7 text-white" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}